/**
 * GA訓練実行スクリプト
 *
 * ブラウザコンソールまたはテストから実行可能。
 * 結果をコンソールに出力し、最適パラメータを確認できる。
 */

import { GeneticTrainer, saveGenome, loadGenome, exportGenome, type TrainingResult } from './index';
import { EVOLVED_GENOME, type AIGenome, cloneGenome, createRandomDeepSeaStrategy } from './genome';
import { createDeckFromPrebuilt } from '../../../utils/helpers';
import { loadCardsFromCSV } from '../../../utils/cardManager';
import { DEFAULT_WEIGHTS, createRandomWeights } from '../evaluation/boardEvaluator';
import { DEFAULT_MAGIC_CONFIG, createRandomMagicConfig } from '../evaluation/magicEvaluator';

/**
 * 小規模テスト訓練（動作確認用）
 */
export async function runQuickTraining(): Promise<TrainingResult> {
  console.log('=== Quick Training (5 individuals, 3 generations) ===');
  console.log('Loading card data...');

  // CSVからカードデータを読み込み
  const allCards = await loadCardsFromCSV();
  console.log(`${allCards.length} cards loaded.`);

  const trainer = new GeneticTrainer({
    populationSize: 5,
    generations: 3,
    gamesPerEvaluation: 4,
    eliteCount: 1,
  });

  const decks = [createDeckFromPrebuilt('gaTraining', allCards)];

  const result = await trainer.train(decks, decks, (gen, best, _pop, log) => {
    console.log(`Gen ${gen}: Best=${best.fitness.toFixed(0)}, Avg=${log.avgFitness.toFixed(0)}`);
  });

  console.log('\n=== Training Complete ===');
  console.log(`Time: ${result.totalTime}ms`);
  console.log(`Best fitness: ${result.bestFitness}`);

  // 全パラメータを出力
  console.log('\n--- Optimized Parameters ---');
  console.log('\n1. attackScoring (攻撃判断):', result.bestGenome.attackScoring);
  console.log('\n2. summonPreference (召喚判断):', result.bestGenome.summonPreference);
  console.log('\n3. skillUsage (スキル使用):', result.bestGenome.skillUsage);
  console.log('\n4. actionProbabilities (行動確率):', result.bestGenome.actionProbabilities);
  console.log('\n5. cardPlacement (カード配置):', result.bestGenome.cardPlacement);
  console.log('\n6. chargeStrategy (チャージ戦略):', result.bestGenome.chargeStrategy);
  console.log('\n7. boardEvaluation (盤面評価):', result.bestGenome.boardEvaluation);
  console.log('\n8. magicEvaluation (魔法評価):', result.bestGenome.magicEvaluation);

  return result;
}

/**
 * 本格訓練（推奨設定）
 */
export async function runFullTraining(): Promise<TrainingResult> {
  console.log('=== Full Training (30 individuals, 50 generations) ===');
  console.log('This may take several minutes...');
  console.log('Loading card data...');

  // CSVからカードデータを読み込み
  const allCards = await loadCardsFromCSV();
  console.log(`${allCards.length} cards loaded.`);

  const trainer = new GeneticTrainer({
    populationSize: 30,
    generations: 50,
    gamesPerEvaluation: 10,
    eliteCount: 3,
    mutationRate: 0.15,
    crossoverRate: 0.7,
  });

  // 強力なデッキを使用
  const decks = [
    createDeckFromPrebuilt('gaTraining', allCards),   // GA訓練用デッキ
    createDeckFromPrebuilt('elizavette', allCards),   // 黒呪の魔女エリザヴェット
    createDeckFromPrebuilt('eclasia', allCards),      // 星灯のエクラシア
    createDeckFromPrebuilt('deepSea', allCards),      // 深海領域デッキ
  ];

  const result = await trainer.train(decks, decks, (gen, best, _pop, log) => {
    if (gen % 5 === 0 || gen === 49) {
      console.log(`Gen ${gen}: Best=${best.fitness.toFixed(0)}, Avg=${log.avgFitness.toFixed(0)}, Diversity=${log.diversity.toFixed(3)}`);
    }
  });

  console.log('\n=== Training Complete ===');
  console.log(`Time: ${(result.totalTime / 1000).toFixed(1)}s`);
  console.log(`Best fitness: ${result.bestFitness}`);

  // 結果を保存
  saveGenome('evolved-ai', result.bestGenome, {
    name: 'Evolved AI',
    fitness: result.bestFitness,
    generations: result.generations,
    evolutionLog: result.evolutionLog,
  });

  console.log('\nGenome saved as "evolved-ai"');

  // 全パラメータを出力
  console.log('\n--- Optimized Parameters ---');
  console.log('\n1. attackScoring (攻撃判断):');
  console.log(JSON.stringify(result.bestGenome.attackScoring, null, 2));
  console.log('\n2. summonPreference (召喚判断):');
  console.log(JSON.stringify(result.bestGenome.summonPreference, null, 2));
  console.log('\n3. skillUsage (スキル使用):');
  console.log(JSON.stringify(result.bestGenome.skillUsage, null, 2));
  console.log('\n4. actionProbabilities (行動確率):');
  console.log(JSON.stringify(result.bestGenome.actionProbabilities, null, 2));
  console.log('\n5. cardPlacement (カード配置):');
  console.log(JSON.stringify(result.bestGenome.cardPlacement, null, 2));
  console.log('\n6. chargeStrategy (チャージ戦略):');
  console.log(JSON.stringify(result.bestGenome.chargeStrategy, null, 2));
  console.log('\n7. boardEvaluation (盤面評価):');
  console.log(JSON.stringify(result.bestGenome.boardEvaluation, null, 2));
  console.log('\n8. magicEvaluation (魔法評価):');
  console.log(JSON.stringify(result.bestGenome.magicEvaluation, null, 2));

  return result;
}

/**
 * Stage 2訓練: シミュレーション評価パラメータ最適化（vs hardAI）
 *
 * 閾値パラメータはEVOLVED_GENOMEから固定し、
 * boardEvaluation/magicEvaluation/deepSeaStrategyを進化させる。
 * **hardAIを対戦相手**として訓練。
 * 強いデッキのみを厳選して使用（深海領域デッキ含む）。
 */
export async function runSimulationParamTraining(): Promise<TrainingResult> {
  console.log('=== Stage 2: Simulation Param Training (vs hardAI) ===');
  console.log('Optimizing boardEvaluation, magicEvaluation & deepSeaStrategy');
  console.log('Threshold params fixed from EVOLVED_GENOME');
  console.log('Opponent: hardAI (fixed baseline)');
  console.log('Loading card data...');

  const allCards = await loadCardsFromCSV();
  console.log(`${allCards.length} cards loaded.`);

  // 強いデッキのみ厳選（AIが使いこなせるデッキ）
  const deckIds = [
    'gaTraining',   // GA訓練用（AIに最適化済み）
    'elizavette',   // 黒呪の魔女エリザヴェット（強力なコンボ）
    'eclasia',      // 星灯のエクラシア（バランス良好）
    'deepSea',      // 深海領域デッキ
  ];

  const decks = deckIds.map(id => createDeckFromPrebuilt(id, allCards));
  console.log(`${decks.length} decks loaded: ${deckIds.join(', ')}`);

  // カスタム初期個体群を作成（閾値固定、シミュレーションパラメータ+深海戦略のみランダム）
  const createSimParamGenome = (): AIGenome => {
    const base = cloneGenome(EVOLVED_GENOME);
    // シミュレーション評価パラメータ + 深海領域戦略をランダム化
    base.boardEvaluation = createRandomWeights();
    base.magicEvaluation = createRandomMagicConfig();
    base.deepSeaStrategy = createRandomDeepSeaStrategy();
    return base;
  };

  // 初期個体群: EVOLVED_GENOME + ランダムシミュレーションパラメータ
  const initialPopulation: AIGenome[] = [
    // デフォルト値を含める
    {
      ...cloneGenome(EVOLVED_GENOME),
      boardEvaluation: { ...DEFAULT_WEIGHTS },
      magicEvaluation: { ...DEFAULT_MAGIC_CONFIG },
    },
  ];
  // 残りはランダム
  for (let i = 1; i < 50; i++) {
    initialPopulation.push(createSimParamGenome());
  }

  const trainer = new GeneticTrainer({
    populationSize: 50,
    generations: 70,
    gamesPerEvaluation: 40,
    eliteCount: 5,
    mutationRate: 0.2,
    crossoverRate: 0.7,
    opponentType: 'hard',  // ★ hardAIを対戦相手に
  });

  // カスタム初期個体群で訓練
  const result = await trainer.trainWithInitialPopulation(
    initialPopulation,
    decks,
    decks,
    (gen, best, _pop, log) => {
      if (gen % 10 === 0 || gen === 69) {
        console.log(`Gen ${gen}: Best=${best.fitness.toFixed(0)}, Avg=${log.avgFitness.toFixed(0)}, Diversity=${log.diversity.toFixed(3)}`);
      }
    }
  );

  console.log('\n=== Stage 2 Training Complete ===');
  console.log(`Time: ${(result.totalTime / 1000).toFixed(1)}s`);
  console.log(`Best fitness: ${result.bestFitness}`);

  // 結果を保存
  saveGenome('evolved-ai-sim', result.bestGenome, {
    name: 'Evolved AI Sim (vs hardAI)',
    fitness: result.bestFitness,
    generations: result.generations,
    evolutionLog: result.evolutionLog,
    description: 'Stage 2: boardEvaluation/magicEvaluation/deepSeaStrategy optimized vs hardAI',
  });

  console.log('\nGenome saved as "evolved-ai-sim"');

  // シミュレーション評価パラメータ + 深海戦略を出力
  console.log('\n--- Optimized Simulation Parameters ---');
  console.log('\nboardEvaluation (盤面評価):');
  console.log(JSON.stringify(result.bestGenome.boardEvaluation, null, 2));
  console.log('\nmagicEvaluation (魔法評価):');
  console.log(JSON.stringify(result.bestGenome.magicEvaluation, null, 2));
  console.log('\ndeepSeaStrategy (深海領域戦略):');
  console.log(JSON.stringify(result.bestGenome.deepSeaStrategy, null, 2));

  return result;
}

// ブラウザコンソール用にグローバルに公開
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).runQuickTraining = runQuickTraining;
  (window as unknown as Record<string, unknown>).runFullTraining = runFullTraining;
  (window as unknown as Record<string, unknown>).runSimulationParamTraining = runSimulationParamTraining;
  (window as unknown as Record<string, unknown>).loadGenome = loadGenome;
  (window as unknown as Record<string, unknown>).exportGenome = exportGenome;
}
